import { openReportWithAuth, postForm } from "../util";
import type { PaginatedResponse } from "./courseCertificates";
import { cleApi, getCleApiPrefix } from "./client";
import type {
  DispensationEvidenceListModel,
  DispensationEvidenceModel,
} from "./models/dispensations";
import axios from "axios";

export type {
  DispensationEvidenceListModel,
  DispensationEvidenceModel,
} from "./models/dispensations";

export interface GetDispensationsParams {
  userId: string;
  page?: number;
  pageSize?: number;
  reported?: boolean;
}

export interface GetDispensationByIdParams {
  userId: string;
  dispensationId: string;
}

export interface CreateDispensationParams {
  userId: string;
  externalEfteruddanelseperiodeId: string;
  numberOfLessons: number;
  file: File;
}

export interface UpdateDispensationRequest {
  number_of_lessons?: number;
  external_efteruddanelseperiode_id?: string;
}

export interface UpdateDispensationParams {
  userId: string;
  dispensationId: string;
  body: UpdateDispensationRequest;
}

export const getDispensations = ({
  userId,
  page = 1,
  pageSize = 20,
  reported,
}: GetDispensationsParams): Promise<
  PaginatedResponse<DispensationEvidenceListModel>
> => {
  const query = new URLSearchParams({
    page: String(page),
    page_size: String(pageSize),
  });

  if (reported !== undefined) {
    query.set("reported", String(reported));
  }

  return cleApi.get<PaginatedResponse<DispensationEvidenceListModel>>(
    `users/${encodeURIComponent(userId)}/dispensations?${query.toString()}`,
  );
};

export const getDispensationById = ({
  userId,
  dispensationId,
}: GetDispensationByIdParams): Promise<DispensationEvidenceModel> =>
  cleApi.get<DispensationEvidenceModel>(
    `users/${encodeURIComponent(userId)}/dispensations/${encodeURIComponent(
      dispensationId,
    )}`,
  );

export const createDispensation = ({
  userId,
  externalEfteruddanelseperiodeId,
  numberOfLessons,
  file,
}: CreateDispensationParams): Promise<DispensationEvidenceModel> => {
  const formData = new FormData();
  formData.append(
    "external_efteruddanelseperiode_id",
    externalEfteruddanelseperiodeId,
  );
  formData.append("number_of_lessons", String(numberOfLessons));
  formData.append("file", file);

  return postForm<DispensationEvidenceModel>({
    apiPrefix: getCleApiPrefix(),
    query: `users/${encodeURIComponent(userId)}/dispensations`,
    body: formData,
  });
};

export const updateDispensation = ({
  userId,
  dispensationId,
  body,
}: UpdateDispensationParams): Promise<DispensationEvidenceModel> =>
  cleApi.put<DispensationEvidenceModel>(
    `users/${encodeURIComponent(userId)}/dispensations/${encodeURIComponent(
      dispensationId,
    )}`,
    body,
  );

export const openDispensationDownload = ({
  userId,
  dispensationId,
}: GetDispensationByIdParams): Promise<void> =>
  openReportWithAuth(
    `${getCleApiPrefix()}users/${encodeURIComponent(
      userId,
    )}/dispensations/${encodeURIComponent(dispensationId)}/download`,
  );

export const getDispensationPdfBlob = async ({
  userId,
  dispensationId,
}: GetDispensationByIdParams): Promise<Blob> => {
  const response = await axios.get<Blob>(
    `${getCleApiPrefix()}users/${encodeURIComponent(
      userId,
    )}/dispensations/${encodeURIComponent(dispensationId)}/download`,
    { responseType: "blob" },
  );

  return response.data;
};
